import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Briefcase, User, Stethoscope, Clock, ChevronDown,
  ChevronUp, RefreshCw, Building, ArrowRight, Shield, CheckCircle
} from 'lucide-react';
import api from '../utils/api';
import { isV3Enabled } from '../utils/featureFlags';

const STATUS_CONFIG = {
  intake: { label: 'Intake', color: 'bg-slate-100 text-slate-700' },
  consultation: { label: 'Consultation', color: 'bg-blue-100 text-blue-700' },
  quoted: { label: 'Quoted', color: 'bg-indigo-100 text-indigo-700' },
  accepted: { label: 'Accepted', color: 'bg-emerald-100 text-emerald-700' },
  scheduled: { label: 'Scheduled', color: 'bg-cyan-100 text-cyan-700' },
  in_treatment: { label: 'In Treatment', color: 'bg-amber-100 text-amber-700' },
  post_treatment: { label: 'Post Treatment', color: 'bg-orange-100 text-orange-700' },
  aftercare: { label: 'Aftercare', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  DEPOSIT_PAID: { label: 'Deposit Paid', color: 'bg-emerald-100 text-emerald-700' },
};

const CaseRow = ({ caseItem, expanded, onToggle, onOpenWorkflow }) => {
  const cfg = STATUS_CONFIG[caseItem.status] || STATUS_CONFIG.intake;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`case-${caseItem.case_id}`}>
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={onToggle}
        data-testid={`case-toggle-${caseItem.case_id}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Briefcase className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">
              {caseItem.treatment_name || 'Untitled Case'}
            </p>
            <p className="text-xs text-slate-500 truncate">
              {caseItem.case_id.slice(0, 8)}... | {caseItem.clinic_id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge className={`text-xs ${cfg.color}`} data-testid={`case-status-${caseItem.case_id}`}>
            {cfg.label}
          </Badge>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50/50 space-y-3" data-testid={`case-detail-${caseItem.case_id}`}>
          <div className="grid grid-cols-2 gap-3 pt-3 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">Person:</span>
              <span className="font-medium truncate">{caseItem.person_id?.slice(0, 12)}...</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">Clinic:</span>
              <span className="font-medium truncate">{caseItem.clinic_id}</span>
            </div>
            <div className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">Doctor:</span>
              <span className="font-medium truncate">{caseItem.assigned_doctor_id || 'Unassigned'}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">Sales:</span>
              <span className="font-medium truncate">{caseItem.assigned_sales_id || 'SYSTEM_POOL'}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">Coordinator:</span>
              <span className="font-medium truncate">{caseItem.assigned_coordinator_id || 'SYSTEM_POOL'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">Created:</span>
              <span className="font-medium">{formatDateLocale(caseItem.created_at)}</span>
            </div>
            {/* V3: Assigned Sales Rep */}
            {isV3Enabled() && caseItem.assigned_sales_rep_id && caseItem.assigned_sales_rep_id !== 'SYSTEM_POOL' && (
              <div className="flex items-center gap-2" data-testid={`v3-case-rep-${caseItem.case_id}`}>
                <Shield className="w-4 h-4 text-blue-400" />
                <span className="text-slate-600">V3 Rep:</span>
                <span className="font-medium truncate text-blue-700">{caseItem.assigned_sales_rep_id.slice(0, 12)}...</span>
              </div>
            )}
            {/* V3: Contact Revealed */}
            {isV3Enabled() && (
              <div className="flex items-center gap-2" data-testid={`v3-case-reveal-${caseItem.case_id}`}>
                <CheckCircle className={`w-4 h-4 ${caseItem.contact_revealed ? 'text-green-500' : 'text-slate-300'}`} />
                <span className="text-slate-600">Contact:</span>
                <span className={`font-medium ${caseItem.contact_revealed ? 'text-green-700' : 'text-slate-400'}`}>
                  {caseItem.contact_revealed ? 'Revealed' : 'Hidden'}
                </span>
              </div>
            )}
          </div>
          {caseItem.lead_id && (
            <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
              <ArrowRight className="w-3 h-3" />
              <span>Linked Lead: {caseItem.lead_id}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CasesPanel = ({ clinicId, title = 'Treatment Cases', compact = false }) => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const loadCases = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (clinicId) params.set('clinic_id', clinicId);
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/api/cases?${params.toString()}`, {
      });
      setCases(res.data.cases || []);
    } catch (err) {
      console.error('Failed to load cases:', err);
    } finally {
      setLoading(false);
    }
  }, [clinicId, statusFilter]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const statusCounts = cases.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card data-testid="cases-panel">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Briefcase className="w-5 h-5" />
          {title}
          <Badge variant="secondary" className="ml-1" data-testid="cases-count">{cases.length}</Badge>
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={loadCases} data-testid="cases-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {!compact && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="cases-filters">
            <Button
              variant={statusFilter === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('')}
              data-testid="filter-all"
            >
              All ({cases.length})
            </Button>
            {Object.entries(statusCounts).map(([s, count]) => (
              <Button
                key={s}
                variant={statusFilter === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-${s}`}
              >
                {STATUS_CONFIG[s]?.label || s} ({count})
              </Button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-slate-500" data-testid="cases-loading">Loading cases...</div>
        ) : cases.length === 0 ? (
          <div className="text-center py-8 text-slate-500" data-testid="cases-empty">No cases found</div>
        ) : (
          <div className="space-y-2" data-testid="cases-list">
            {cases.slice(0, compact ? 5 : 50).map((c) => (
              <CaseRow
                key={c.case_id}
                caseItem={c}
                expanded={expandedId === c.case_id}
                onToggle={() => setExpandedId(expandedId === c.case_id ? null : c.case_id)}
                onOpenWorkflow={(cid) => navigate(`/clinic/cases/${cid}/workflow`)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CasesPanel;
